import { useSelector } from 'react-redux';
import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { invalidateCropMonitoringQueries } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { axFetch } from '/@/store/session/actions';
import { getSessionCountry } from '/@/store/session/selectors';

type ReportType = 'harvest' | 'planting' | 'application';

export const getFvGrowingSeasons = async ({ fieldId }: { fieldId: number }) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/fields/me/${fieldId}/fv-growing-seasons/`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useFvGrowingSeasons = (
    {
        fieldId,
    }: {
        fieldId?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && fieldId) key = { fieldId, key: 'useFvGrowingSeasons' };

    return useSWR(key, getFvGrowingSeasons);
};

export const createCropReport = async ({
    field,
    type,
}: {
    field: number;
    type: 'harvest' | 'planting' | 'application';
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/reports/me/${type}/create-all/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), {
        field_id: field,
    });
    return res.data;
};

export const useCreateCropReport = ({ field, type }: { field: number; type: ReportType }) => {
    let key = null;
    if (field && type) key = { key: 'useCreateCropReport', field, type };
    return useSWRMutation(key, () => createCropReport({ field, type }), {
        onSuccess: () => {
            mutate((key) => key?.fieldId === field && key?.type === type && key?.key == 'useReports');
        },
    });
};

export const getReports = async ({ fieldId, type }: { fieldId: number; type: ReportType }) => {
    const params = new URLSearchParams();
    if (fieldId) params.append('field_id', String(fieldId));

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/reports/me/${type}/?${params.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useReports = (
    {
        fieldId,
        type,
    }: {
        fieldId?: number;
        type: ReportType;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && fieldId && type) key = { fieldId, type, key: 'useReports' };

    return useSWR(key, getReports);
};

/**
 * Bulk version: fetch planting (or any) reports for many fields in ONE request.
 * Uses `field_id__in=1,2,3` — results are returned as a flat list; callers index by field_id.
 */
export const getReportsBulk = async ({ fieldIds, type }: { fieldIds: number[]; type: ReportType }) => {
    const params = new URLSearchParams();
    params.append('field_id__in', fieldIds.join(','));
    params.append('page_size', '500');

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/reports/me/${type}/?${params.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useReportsBulk = (
    { fieldIds, type }: { fieldIds: number[]; type: ReportType },
    { enabled = true }: { enabled?: boolean } = {}
) => {
    const sortedIds = [...fieldIds].sort((a, b) => a - b);
    const key = enabled && sortedIds.length > 0 ? { fieldIds: sortedIds, type, key: 'useReportsBulk' } : null;
    // FieldView data doesn't change within a session; avoid refetching on field list remount.
    return useSWR(key, getReportsBulk, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const updateHumidityAtHarvestCropMonitoringResult = async ({
    cropMonitoringId,
    humidityAtHarvest,
}: {
    cropMonitoringId: number;
    humidityAtHarvest?: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/result/`,
        window.location.origin
    );

    const res = await axFetch.patch(apiUrl.toString(), {
        humidityAtHarvest: humidityAtHarvest,
    });
    return res.data;
};

export const createOrUpdateCropMonitoringResult = async ({
    cropMonitoringId,
    plantingDate,
    harvestDate,
    humidityAtHarvest,
    averageYield,
    plantingReport,
    harvestReport,
}: {
    cropMonitoringId: number;
    plantingDate?: string;
    harvestDate?: string;
    humidityAtHarvest?: number;
    averageYield?: number;
    plantingReport?: number;
    harvestReport?: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/result/`,
        window.location.origin
    );

    const res = await axFetch.put(apiUrl.toString(), {
        plantingDate: plantingDate,
        harvestDate: harvestDate,
        humidityAtHarvest: humidityAtHarvest,
        averageYield: averageYield,
        planting_report: plantingReport,
        harvest_report: harvestReport,
    });
    return res.data;
};

export const useCreateOrUpdateCropMonitoringResult = ({
    cropMonitoringId,
    fieldId,
}: {
    cropMonitoringId: number;
    fieldId: number;
}) => {
    const country = useSelector(getSessionCountry);

    let key = null;
    if (cropMonitoringId) key = { key: 'useCreateCropReport', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    plantingDate?: string;
                    harvestDate?: string;
                    humidityAtHarvest?: number;
                    averageYield?: number;
                    plantingReport?: number;
                    harvestReport?: number;
                };
            }
        ) => createOrUpdateCropMonitoringResult({ cropMonitoringId, ...arg }),
        {
            onSuccess: () => {
                // Use centralized invalidation function
                invalidateCropMonitoringQueries({
                    cmId: cropMonitoringId,
                    fieldId: fieldId,
                    country: country.code,
                });
            },
        }
    );
};

export const useUpdateHumidityAtHarvestCropMonitoringResult = ({
    cropMonitoringId,
    fieldId,
}: {
    cropMonitoringId: number;
    fieldId: number;
}) => {
    const country = useSelector(getSessionCountry);

    let key = null;
    if (cropMonitoringId) key = { key: 'useUpdateHumidityAtHarvestCropMonitoringResult', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    humidityAtHarvest?: number;
                };
            }
        ) => updateHumidityAtHarvestCropMonitoringResult({ cropMonitoringId, ...arg }),
        {
            onSuccess: () => {
                // Use centralized invalidation function
                invalidateCropMonitoringQueries({
                    cmId: cropMonitoringId,
                    fieldId: fieldId,
                    country: country.code,
                });
            },
        }
    );
};

export const getCropMonitoringResultNotes = async ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/notes/?ordering=-created`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCropMonitoringResultNotes = (
    {
        cropMonitoringId,
    }: {
        cropMonitoringId?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    let key = null;
    if (enabled && cropMonitoringId) key = { cropMonitoringId, key: 'useCropMonitoringResultNotes' };

    return useSWR(key, getCropMonitoringResultNotes);
};

export const createOrUpdateCropMonitoringResultNote = async ({
    id,
    cropMonitoringId,
    content,
}: {
    id?: number;
    cropMonitoringId: number;
    content: string;
}) => {
    if (id) {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/notes/${id}/`,
            window.location.origin
        );

        const res = await axFetch.patch(apiUrl.toString(), {
            content,
        });
        return res.data;
    }

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/notes/`,
        window.location.origin
    );
    const res = await axFetch.post(apiUrl.toString(), {
        content,
    });
    return res.data;
};

export const useCreateOrUpdateCropMonitoringResultNote = ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    let key = null;
    if (cropMonitoringId) key = { key: 'useCreateOrUpdateCropMonitoringResultNote', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    content: string;
                    id?: number;
                };
            }
        ) => createOrUpdateCropMonitoringResultNote({ cropMonitoringId, ...arg }),
        {
            onSuccess: () => {
                mutate((key) => {
                    const useCropMonitoringResultNotes =
                        key?.cropMonitoringId === cropMonitoringId && key?.key == 'useCropMonitoringResultNotes';

                    return useCropMonitoringResultNotes;
                });
            },
        }
    );
};

export const deleteCropMonitoringResultNote = async ({
    id,
    cropMonitoringId,
}: {
    id: number;
    cropMonitoringId: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/notes/${id}/`,
        window.location.origin
    );

    const res = await axFetch.delete(apiUrl.toString());
    return res.data;
};

export const useDeleteCropMonitoringResultNote = ({ cropMonitoringId }: { cropMonitoringId: number }) => {
    let key = null;
    if (cropMonitoringId) key = { key: 'useDeleteCropMonitoringResultNote', cropMonitoringId };
    return useSWRMutation(
        key,
        (
            { cropMonitoringId },
            {
                arg,
            }: {
                arg: {
                    id: number;
                };
            }
        ) => deleteCropMonitoringResultNote({ cropMonitoringId, ...arg }),
        {
            onSuccess: () => {
                mutate((key) => {
                    const useCropMonitoringResultNotes =
                        key?.cropMonitoringId === cropMonitoringId && key?.key == 'useCropMonitoringResultNotes';

                    return useCropMonitoringResultNotes;
                });
            },
        }
    );
};
